/** Chunk/DlView 변경 감지 + 메모이제이션 레이어 (순수 함수, React 비의존) */

import type { IxdChunk, PeriodType } from "@/apis/compare/route";
import type { TaggedValue } from "@/apis/compare/types";
import {
  buildCellIdAliasesFromTaggedValue,
  buildCellIdFromTableColumn,
  buildCellIdLookupCandidates,
  buildCellIdentityMapFromValues,
  normalizeCellId,
  parseCellIdChangeKey,
  parseIdentityChangeKey,
  type CellChangeOrigin,
  type CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import type { TableCellColumn } from "../table-cell";
import type { IxdDlView } from "./cell-lookup";

const chunkCellIdsCache = new WeakMap<IxdChunk, Set<string>>();
const chunkIdentityKeysCache = new WeakMap<
  IxdChunk,
  {
    cellIdentityByNormalizedCellId: Map<string, string> | undefined;
    identityKeys: Set<string>;
  }
>();
const dlViewCellIdsCache = new WeakMap<IxdDlView, Set<string>>();
const dlViewIdentityKeysCache = new WeakMap<
  IxdDlView,
  {
    cellIdentityByNormalizedCellId: Map<string, string> | undefined;
    identityKeys: Set<string>;
  }
>();

export const getDlViewNormalizedCellIds = (dlView: IxdDlView): Set<string> => {
  const cached = dlViewCellIdsCache.get(dlView);
  if (cached) return cached;

  const ids = new Set<string>();
  if (dlView.table) {
    for (const [periodType, rows] of Object.entries(dlView.table) as [
      PeriodType,
      Array<{ columns: TableCellColumn[] }>,
    ][]) {
      for (const row of rows) {
        for (const column of row.columns) {
          if (!column.id) continue;
          ids.add(normalizeCellId(column.id));
          const canonicalCellId = buildCellIdFromTableColumn(
            periodType,
            column,
          );
          for (const candidate of buildCellIdLookupCandidates(
            periodType,
            canonicalCellId,
            column.id,
            column.attributes.contextKey,
          )) {
            ids.add(candidate);
          }
        }
      }
    }
  }

  if (dlView.values) {
    for (const [periodType, taggedValues] of Object.entries(dlView.values) as [
      PeriodType,
      TaggedValue[],
    ][]) {
      for (const taggedValue of taggedValues) {
        for (const alias of buildCellIdAliasesFromTaggedValue(
          periodType,
          taggedValue,
        )) {
          ids.add(alias);
        }
      }
    }
  }

  dlViewCellIdsCache.set(dlView, ids);
  return ids;
};

export const getDlViewIdentityKeys = (
  dlView: IxdDlView,
  cellIdentityByNormalizedCellId: Map<string, string> | undefined,
): Set<string> => {
  const cached = dlViewIdentityKeysCache.get(dlView);
  if (
    cached &&
    cached.cellIdentityByNormalizedCellId === cellIdentityByNormalizedCellId
  ) {
    return cached.identityKeys;
  }

  const identityKeys = new Set<string>();
  for (const normalizedCellId of getDlViewNormalizedCellIds(dlView)) {
    const identityKey = cellIdentityByNormalizedCellId?.get(normalizedCellId);
    if (identityKey) identityKeys.add(identityKey);
  }
  const dlViewIdentityMap = buildCellIdentityMapFromValues(dlView.values);
  for (const identityKey of dlViewIdentityMap.values()) {
    identityKeys.add(identityKey);
  }

  dlViewIdentityKeysCache.set(dlView, {
    cellIdentityByNormalizedCellId,
    identityKeys,
  });
  return identityKeys;
};

export const getChunkNormalizedCellIds = (chunk: IxdChunk): Set<string> => {
  const cached = chunkCellIdsCache.get(chunk);
  if (cached) return cached;

  const ids = new Set<string>();
  for (const dlView of chunk.dlViews) {
    for (const id of getDlViewNormalizedCellIds(dlView)) {
      ids.add(id);
    }
  }

  chunkCellIdsCache.set(chunk, ids);
  return ids;
};

export const getRelevantRecordChanges = (
  changes: Record<string, string> | undefined,
  normalizedCellIds: Set<string>,
): Map<string, string> => {
  const relevant = new Map<string, string>();
  if (!changes) return relevant;
  for (const [cellId, value] of Object.entries(changes)) {
    const normalizedCellId = normalizeCellId(cellId);
    if (normalizedCellIds.has(normalizedCellId)) {
      relevant.set(normalizedCellId, value);
    }
  }
  return relevant;
};

export const areStringMapsEqual = (
  previous: Map<string, string>,
  next: Map<string, string>,
): boolean => {
  if (previous.size !== next.size) return false;
  for (const [key, value] of previous) {
    if (next.get(key) !== value) return false;
  }
  return true;
};

export const getChunkIdentityKeys = (
  chunk: IxdChunk,
  cellIdentityByNormalizedCellId: Map<string, string> | undefined,
): Set<string> => {
  const cached = chunkIdentityKeysCache.get(chunk);
  if (
    cached &&
    cached.cellIdentityByNormalizedCellId === cellIdentityByNormalizedCellId
  ) {
    return cached.identityKeys;
  }

  const identityKeys = new Set<string>();
  for (const normalizedCellId of getChunkNormalizedCellIds(chunk)) {
    const identityKey = cellIdentityByNormalizedCellId?.get(normalizedCellId);
    if (identityKey) identityKeys.add(identityKey);
  }
  for (const dlView of chunk.dlViews) {
    const dlViewIdentityMap = buildCellIdentityMapFromValues(dlView.values);
    for (const identityKey of dlViewIdentityMap.values()) {
      identityKeys.add(identityKey);
    }
  }
  chunkIdentityKeysCache.set(chunk, {
    cellIdentityByNormalizedCellId,
    identityKeys,
  });
  return identityKeys;
};

export const getRelevantIdentityChanges = (
  identityChanges: Map<string, string> | undefined,
  identityKeys: Set<string>,
): Map<string, string> => {
  const relevant = new Map<string, string>();
  if (!identityChanges) return relevant;
  for (const [identityKey, value] of identityChanges) {
    if (identityKeys.has(identityKey)) relevant.set(identityKey, value);
  }
  return relevant;
};

export const getRelevantChangeOrigins = (
  origins: Record<string, CellChangeOrigin> | undefined,
  normalizedCellIds: Set<string>,
  identityKeys: Set<string>,
): Map<string, string> => {
  const relevant = new Map<string, string>();
  if (!origins) return relevant;

  for (const [changeKey, origin] of Object.entries(origins)) {
    const identityKey = parseIdentityChangeKey(changeKey);
    if (identityKey) {
      if (identityKeys.has(identityKey)) relevant.set(changeKey, origin);
      continue;
    }

    const normalizedCellId = parseCellIdChangeKey(changeKey);
    if (normalizedCellId && normalizedCellIds.has(normalizedCellId)) {
      relevant.set(changeKey, origin);
    }
  }
  return relevant;
};

export const getRelatedIdentityCount = (
  identityKey: string | null | undefined,
  identityLocationsByKey:
    | ReadonlyMap<string, readonly CellIdentityLocation[]>
    | null
    | undefined,
): number | undefined => {
  if (!identityKey) return undefined;
  const count = identityLocationsByKey?.get(identityKey)?.length ?? 0;
  return Math.max(count - 1, 0);
};
